import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { CSSProperties } from "react";
import { AboTerminBlatt } from "../components/planer/AboTerminBlatt";
import { AktivitaetPanel } from "../components/planer/AktivitaetPanel";
import { DayPanel } from "../components/planer/DayPanel";
import { EditTerminDrawer } from "../components/planer/EditTerminDrawer";
import { TagesAnsicht } from "../components/planer/TagesAnsicht";
import { MONTHS, WD, WD_FULL, addDays, colorFor, datumAusLink, firstOfMonth, hhmm, iso, istBesichtigung, istSchmal, kopierteSlots, mondayOf, usePeople } from "../components/planer/planer-utils";
import { useAktivitaet, useCreateTermin, useKalender, useKunden, useProjekte, useTeamMembers, useTeamOwners, useUpdateTermin } from "../hooks/usePlanung";
import { useIstTelefon } from "../hooks/useIstTelefon";
import { useNotizAendern } from "../hooks/useNotizen";
import { aboTermineProTag, useAboTermine, useKalenderAbos } from "../hooks/useKalenderAbos";
import type { AboTermin } from "../hooks/useKalenderAbos";
import type { NewTerminBody } from "../hooks/usePlanung";
import { formatEur, slotStundenNetto } from "../lib/planer";
import { errorText } from "../lib/invoices";
import type { Termin } from "../lib/types";
import ZuPlanenLeiste from "../components/planer/ZuPlanenLeiste";
import PlanerKopf from "../components/planer/PlanerKopf";
import { PlanerTitel } from "../components/planer/Kpi";
import { usePlanerSperren } from "../components/planer/SperrBlatt";
import { SperrStreifen } from "../components/planer/SperrZeilen";
import { useKonfliktFrage } from "../components/planer/KonfliktFrage";
import { mitAntwort, mitKonflikten } from "../lib/terminKonflikt";
import "../styles/planung.css";
import "../styles/kalenderabo.css";

export default function PlanungPage() {
  const [startDatum] = useState(datumAusLink);
  const [month, setMonth] = useState<Date>(() => firstOfMonth(startDatum ? new Date(`${startDatum}T12:00:00`) : new Date()));
  const [edit, setEdit] = useState<Termin | null>(null);
  /* Am Rechner öffnet ein Datum aus dem Link gleich den Tag; am Telefon steht er in der Tagesansicht. */
  const [dayOpen, setDayOpen] = useState<string | null>(() => (startDatum && !istSchmal() ? startDatum : null));
  /* Telefon: der Tipp auf einen Tag wählt ihn aus, die Termine stehen darunter
     (TagesAnsicht). Das Blatt mit dem Entwurf öffnet erst „＋ Termin" — dann
     nur zum Anlegen (nurNeu). Am Rechner bleibt alles wie es war. */
  const telefon = useIstTelefon();
  const [gewaehlt, setGewaehlt] = useState<string>(() => startDatum || iso(new Date()));
  const [nurNeu, setNurNeu] = useState(false);
  const [aboOffen, setAboOffen] = useState<AboTermin | null>(null);
  const [aktOpen, setAktOpen] = useState(false);
  // Nur der Zaehler wird hier gebraucht; die Liste holt sich das Panel selbst
  // aus demselben Query-Eintrag, ohne zweite Anfrage.
  const { data: aktivitaet } = useAktivitaet();
  const offeneAkt = aktivitaet?.offen_count ?? 0;

  const gridStart = useMemo(() => mondayOf(firstOfMonth(month)), [month]);
  const gridDays = useMemo(() => Array.from({ length: 42 }, (_, i) => addDays(gridStart, i)), [gridStart]);
  const { data: termineRaw = [], isLoading, isError } = useKalender(iso(gridStart), iso(gridDays[41]));
  /* Abonnierte Kalender: Telefon zeigt sie unter dem Kalender, der Rechner im
     Tagesblatt unter den Kontor-Terminen. Im Monatsraster stehen sie nie. */
  const { data: abos = [] } = useKalenderAbos();
  const hatAbos = abos.some((a) => a.aktiv);
  const aboQuery = useAboTermine(iso(gridStart), iso(gridDays[41]), hatAbos);
  const aboProTag = useMemo(() => aboTermineProTag(aboQuery.data?.termine ?? []), [aboQuery.data]);
  /* Wer nicht da ist (16.10.2026): Streifen im Raster, Zeile im Tag, Blatt „Nicht da". */
  const sperren = usePlanerSperren(iso(gridStart), iso(gridDays[41]));
  const aboFuer = (tag: string) => ({
    termine: aboProTag.get(tag) ?? [],
    fehler: aboQuery.data?.fehler ?? [],
    hatAbos,
    laedt: hatAbos && aboQuery.isPending,
  });
  const { data: members = [] } = useTeamMembers();
  const { data: owners = [] } = useTeamOwners();
  /* include_all: abgeschlossene und verlorene Projekte gehoeren in Filter UND
     Terminanlage. Ohne sie liessen sich Stunden auf einer beendeten Baustelle
     nicht nachtragen — und genau das ist der haeufigste Nachpflegefall. */
  const { data: projekte = [] } = useProjekte(true);
  /* Fuer Besichtigungen: die hängen am Kunden, nicht am Projekt. */
  const { data: kunden = [] } = useKunden();

  /* Aus einer Aufgabe heraus planen (/planung?planen=1&kunde=…&projekt=…&notiz=…):
     Kopfzeile mit dem Namen, der Tag wird im Kalender gewählt, Projekt oder
     Kunde stehen im Formular schon drin. Die Notiz merkt sich den Termin — so
     kann aus der Besichtigung später ein Projekt werden, das ihn mitnimmt. */
  const [such, setSuch] = useSearchParams();
  const planen = such.get("planen") === "1";
  const planKunde = such.get("kunde") || "";
  const planProjekt = such.get("projekt") || "";
  const planNotiz = such.get("notiz") || "";
  const vorgabe = planen ? { kundeId: planKunde || undefined, projektId: planProjekt || undefined } : undefined;
  const planName = planProjekt
    ? projekte.find((p) => p.id === planProjekt)?.title || ""
    : kunden.find((k) => k.id === planKunde)?.name || "";
  const [geplant, setGeplant] = useState("");
  const notizAendern = useNotizAendern();
  const planenBeenden = () => setSuch({}, { replace: true });
  const terminAngelegt = (t: Termin) => {
    if (planNotiz && t.id) notizAendern.mutate({ id: planNotiz, bezug: { termin_id: t.id } });
    setGeplant((t.start_at || "").slice(0, 10));
    planenBeenden();
  };
  const people = usePeople(members, owners);
  const verschieben = useUpdateTermin();
  const anlegen = useCreateTermin();
  /* Landet ein Termin auf jemandem, der schon eingeplant ist, wird gefragt. */
  const kopierFrage = useKonfliktFrage();
  const schiebFrage = useKonfliktFrage();
  const [legeFehler, setLegeFehler] = useState("");
  /* Ziehen im Kalender: Termin-ID, die gerade am Mauszeiger haengt, plus das
     Zieldatum unter dem Zeiger fuer die Hervorhebung der Zelle. */
  const [zieht, setZieht] = useState<Termin | null>(null);
  const [zielTag, setZielTag] = useState<string | null>(null);
  /* Aufnehmen und Ablegen — der Weg fürs Telefon. Dort sind die Chips zu klein
     zum Ziehen und nehmen bewusst keine Zeiger-Events an (planung.css); der
     Termin wird stattdessen in der Tagesliste aufgenommen und liegt dann am
     Finger, bis ein Zieltag angetippt wird. Der Monat lässt sich dabei
     wechseln, also geht auch ein Sprung über die Monatsgrenze. */
  /* ausTermin: aus dem geöffneten Termin aufgenommen — „Abbrechen" führt dann
     dorthin zurück, nicht auf den Kalender (eine Ebene, 14.10.2026). */
  const [aufgenommen, setAufgenommen] = useState<{ t: Termin; modus: "verschieben" | "kopieren"; ausTermin?: boolean } | null>(null);

  /** Termin auf einen anderen Tag legen — verschieben oder (mit Alt) kopieren. */
  const aufTagLegen = (t: Termin, ziel: string, kopieren: boolean) => {
    const quelle = (t.start_at || "").slice(0, 10);
    /* Derselbe Tag ist nur beim Verschieben sinnlos. Eine Kopie am selben Tag
       ist ein echter Fall — der zweite Einsatz am Nachmittag. */
    if (!ziel || (!kopieren && ziel === quelle)) return;
    const neuStart = `${ziel}T${(t.start_at || "").slice(11, 19) || "08:00:00"}`;
    const neuEnde = `${ziel}T${(t.end_at || "").slice(11, 19) || "16:00:00"}`;
    const melden = (e: unknown) => setLegeFehler(errorText(e));
    setLegeFehler("");
    if (kopieren) {
      const body: NewTerminBody = {
        projekt_id: t.projekt_id || "",
        // Ohne kunde_id verlöre eine kopierte Besichtigung ihren einzigen
        // Bezug und der Server wiese sie mit 400 ab.
        kunde_id: t.projekt_id ? "" : (t.kunde_id || ""),
        planung_typ: istBesichtigung(t) ? "besichtigung" : "termin",
        title: t.title || t.projekt_title || "Einsatz",
        start_at: neuStart,
        end_at: neuEnde,
        notiz: t.notiz || "",
        benoetigte_helfer: t.benoetigte_helfer || 0,
        offene_stellen: t.offene_stellen || 0,
        slots: kopierteSlots(t, ziel),
      };
      mitKonflikten((a) => anlegen.mutateAsync(mitAntwort(body, a)), kopierFrage.fragen).catch(melden);
    } else {
      /* Nur start_at/end_at schicken, KEINE slots: das Backend zieht die
         vorhandenen Slots dann um dieselbe Differenz mit (siehe
         planer.planung_patch). Schickten wir slots mit, ersetzte es sie —
         und Bestaetigungen und Slot-IDs waeren weg. */
      mitKonflikten(
        (a) => verschieben.mutateAsync({ id: t.id, patch: { start_at: neuStart, end_at: neuEnde, ...(a ? { konflikt_loesungen: a.loesungen } : {}) } }),
        schiebFrage.fragen,
      ).catch(melden);
    }
  };

  /** Monat wechseln — die Auswahl springt auf heute, wenn er darin liegt, sonst auf den Ersten. */
  const monatZeigen = (m: Date) => {
    setMonth(m);
    const heute = new Date();
    setGewaehlt(iso(heute.getMonth() === m.getMonth() && heute.getFullYear() === m.getFullYear() ? heute : m));
  };

  /** Tag angetippt: Telefon wählt aus (ein Tag aus dem Nachbarmonat blättert mit), Rechner öffnet das Blatt. */
  const tagAntippen = (d: Date) => {
    const k = iso(d);
    if (aufgenommen) { ablegenAuf(k); return; }
    if (!telefon) { setDayOpen(k); return; }
    if (d.getMonth() !== month.getMonth()) setMonth(firstOfMonth(d));
    setGewaehlt(k);
  };

  /** Aufgenommenen Termin auf dem angetippten Tag ablegen. */
  const ablegenAuf = (ziel: string) => {
    if (!aufgenommen) return;
    aufTagLegen(aufgenommen.t, ziel, aufgenommen.modus === "kopieren");
    setAufgenommen(null);
  };

  const termine = termineRaw;
  const byDay = useMemo(() => {
    const m = new Map<string, Termin[]>();
    for (const t of termine) { const k = (t.start_at || "").slice(0, 10); const arr = m.get(k) ?? []; arr.push(t); m.set(k, arr); }
    for (const arr of m.values()) arr.sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""));
    return m;
  }, [termine]);

  const monthTermine = useMemo(
    () => termine.filter((t) => { const d = new Date(t.start_at); return d.getMonth() === month.getMonth() && d.getFullYear() === month.getFullYear(); }),
    [termine, month],
  );
  const personalMonth = useMemo(() => monthTermine.reduce((s, t) => s + t.slots.reduce((a, sl) => a + slotStundenNetto(sl) * (sl.stundenlohn || 0), 0), 0), [monthTermine]);
  const offeneSlots = useMemo(() => termine.reduce((s, t) => s + (t.freie_stellen ?? t.offene_stellen ?? 0), 0), [termine]);
  const todayISO = iso(new Date());
  const in14 = iso(addDays(new Date(), 14));
  const upcoming = useMemo(() => termine.filter((t) => { const k = (t.start_at || "").slice(0, 10); return k >= todayISO && k <= in14; }), [termine, todayISO, in14]);
  const ohneSlot = useMemo(() => upcoming.filter((t) => t.slots.length === 0).length, [upcoming]);

  return (
    <div className="pl-wrap">
      {legeFehler && (
        <div className="pl-legefehler" role="alert">
          <span>{legeFehler}</span>
          <button type="button" className="pl-planen-link" onClick={() => setLegeFehler("")}>Schließen</button>
        </div>
      )}
      {(planen || geplant) && (
        <div className="pl-planen" role="status">
          {geplant ? (
            <span>Termin am {new Date(`${geplant}T12:00:00`).toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" })} angelegt.</span>
          ) : (
            <span>
              Termin für <strong>{planName || "…"}</strong> planen — {telefon ? "Tag wählen, dann „＋ Termin“." : "Tag im Kalender anklicken."}
            </span>
          )}
          <span className="pl-planen-knoepfe">
            <Link to="/aufgaben" className="pl-planen-link">Zu den Aufgaben</Link>
            {!geplant && <button type="button" className="pl-planen-link" onClick={planenBeenden}>Abbrechen</button>}
          </span>
        </div>
      )}
      {/* Beauftragt, aber kein Einsatz im Kalender (Etappe 3) — nur außerhalb des Planens. */}
      {!planen && !geplant && <ZuPlanenLeiste />}
      <PlanerTitel
        personalkosten={formatEur(personalMonth, { round: true })} offeneSlots={offeneSlots}
        kommend={upcoming.length} ohneSlot={ohneSlot}
        helfer={people.filter((p) => p.role === "helfer").length} inhaber={people.filter((p) => p.role === "inhaber").length}
      />

      <PlanerKopf
        month={month}
        heuteUnnoetig={month.getMonth() === new Date().getMonth() && month.getFullYear() === new Date().getFullYear() && gewaehlt === todayISO}
        offeneAkt={offeneAkt}
        onMonat={monatZeigen}
        onAktivitaet={() => setAktOpen(true)}
        onNeu={() => { if (telefon) { setNurNeu(true); setDayOpen(gewaehlt); } else setDayOpen(todayISO); }}
      />

      {isError && <p style={{ color: "var(--vg-red)" }}>Planung konnte nicht geladen werden.</p>}

      {aufgenommen && (
        <div className="pl-ablegen" role="status">
          <span className="pl-ablegen-was">
            <strong>{aufgenommen.modus === "kopieren" ? "Kopieren" : "Verschieben"}</strong>
            <span>{aufgenommen.t.projekt_title || aufgenommen.t.title || "Termin"}</span>
          </span>
          <span className="pl-ablegen-wie">Zieltag antippen</span>
          <button type="button" className="pl-ablegen-ab" onClick={() => { if (aufgenommen.ausTermin) setEdit(aufgenommen.t); setAufgenommen(null); }}>
            {aufgenommen.ausTermin ? "‹ Zurück zum Termin" : "Abbrechen"}
          </button>
        </div>
      )}

      <div className="pl-calcard">
        <div className="pl-grid">
          {WD.map((w) => <div key={w} className="pl-wd"><span className="pl-wd-long">{WD_FULL[w]}</span><span className="pl-wd-short">{w}</span></div>)}
          {gridDays.map((d) => {
            const k = iso(d);
            const out = d.getMonth() !== month.getMonth();
            const isToday = k === todayISO;
            const items = byDay.get(k) ?? [];
            return (
              <div key={k}
                className={`pl-cell${out ? " pl-cell-out" : ""}${isToday ? " pl-cell-today" : ""}${zielTag === k && zieht ? " pl-cell-drop" : ""}${aufgenommen ? " pl-cell-ziel" : ""}${telefon && k === gewaehlt ? " pl-cell-gewaehlt" : ""}`}
                onClick={() => tagAntippen(d)}
                title={aufgenommen ? "Termin auf diesen Tag legen" : "Termin an diesem Tag anlegen"}
                /* Ziehen = verschieben, Alt-Ziehen = kopieren. dropEffect setzt
                   nur den Mauszeiger; was wirklich passiert, entscheidet der
                   altKey beim Loslassen. */
                onDragOver={(e) => {
                  if (!zieht) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = e.altKey ? "copy" : "move";
                  if (zielTag !== k) setZielTag(k);
                }}
                onDragLeave={() => { if (zielTag === k) setZielTag(null); }}
                onDrop={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  const t = zieht;
                  setZieht(null); setZielTag(null);
                  if (t) aufTagLegen(t, k, e.altKey);
                }}>
                <div className="pl-cell-head">
                  {/* Der Tagesknopf traegt die Tastatur- und Screenreader-Bedienung.
                      Die Zelle selbst ist bewusst KEIN role="button" mehr — sonst
                      steckten die Termin-Chips in einem Bedienelement. */}
                  <button
                    type="button"
                    className="pl-daynum"
                    aria-label={aufgenommen
                      ? `${d.getDate()}. ${MONTHS[d.getMonth()]} — Termin hierher ${aufgenommen.modus === "kopieren" ? "kopieren" : "verschieben"}`
                      : `${d.getDate()}. ${MONTHS[d.getMonth()]} — ${items.length === 0 ? "keine Termine" : `${items.length} Termin(e)`}, Tag oeffnen`}
                    aria-pressed={telefon ? k === gewaehlt : undefined}
                    onClick={(e) => { e.stopPropagation(); tagAntippen(d); }}
                  >{d.getDate()}</button>
                  {isToday && <span className="pl-today-tag">HEUTE</span>}
                </div>
                <div className="pl-chips">
                  {items.map((t) => {
                    const c = t.farbe && /^#/.test(t.farbe) ? t.farbe : colorFor(t.projekt_id || t.id, "helfer");
                    const hrs = t.slots.reduce((a, sl) => a + slotStundenNetto(sl), 0);
                    const empty = t.slots.length === 0;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        draggable
                        onDragStart={(e) => {
                          setZieht(t);
                          e.dataTransfer.effectAllowed = "copyMove";
                          // Firefox startet ohne gesetzte Nutzlast gar kein Ziehen.
                          e.dataTransfer.setData("text/plain", t.id);
                        }}
                        onDragEnd={() => { setZieht(null); setZielTag(null); }}
                        onClick={(e) => { e.stopPropagation(); setEdit(t); }}
                        title={`${istBesichtigung(t) ? "Besichtigung: " : ""}${t.projekt_title || t.title} — ${t.kunde_name}\nZiehen = verschieben · Alt+Ziehen = kopieren`}
                        className={`pl-chip${istBesichtigung(t) ? " pl-chip-besichtigung" : ""}`}
                        style={{ "--pl-chip-color": c } as CSSProperties}
                      >
                        <span className="pl-chip-time">{hhmm(t.start_at)}</span>
                        <span className="pl-chip-title">
                          {/* Besichtigungen tragen kein Projekt — ohne Zeichen
                              waeren sie im Monat nicht von Einsaetzen zu
                              unterscheiden. */}
                          {istBesichtigung(t) ? "👁 " : ""}
                          {t.projekt_title || t.title || "Termin"}
                        </span>
                        <span className={`pl-chip-hrs${empty ? " pl-chip-empty" : ""}`}>{empty ? "leer" : `${Math.round(hrs)}H`}</span>
                      </button>
                    );
                  })}
                </div>
                <SperrStreifen tag={k} sperren={sperren.fuer(k)} />
              </div>
            );
          })}
        </div>
        {isLoading && <div style={{ padding: 16, color: "var(--vg-text-3)" }}>Lade Planung…</div>}
      </div>

      {telefon && (
        <TagesAnsicht
          dateIso={gewaehlt}
          termine={byDay.get(gewaehlt) ?? []}
          abo={aboFuer(gewaehlt)}
          onTermin={setEdit}
          onAbo={setAboOffen}
          onNeu={() => { setNurNeu(true); setDayOpen(gewaehlt); }}
          onAufnehmen={(t, modus) => setAufgenommen({ t, modus })}
          sperren={sperren.fuer(gewaehlt)}
          onSperre={(s) => sperren.oeffnen(s, gewaehlt)}
          onNichtDa={() => sperren.neu(gewaehlt)}
          onBuerotag={() => sperren.neu(gewaehlt, "buero")}
        />
      )}

      {dayOpen !== null && (
        <DayPanel
          key={dayOpen}
          nurNeu={nurNeu}
          abo={aboFuer(dayOpen)}
          onAbo={setAboOffen}
          dateIso={dayOpen} projekte={projekte} kunden={kunden} people={people}
          termine={byDay.get(dayOpen) ?? []}
          onEdit={(t) => { setEdit(t); setDayOpen(null); }}
          onAufnehmen={(t, modus) => { setAufgenommen({ t, modus }); setDayOpen(null); }}
          onClose={() => { setDayOpen(null); setNurNeu(false); }}
          vorgabe={vorgabe}
          onAngelegt={planen ? terminAngelegt : undefined}
          sperren={sperren.fuer(dayOpen)} onSperre={(s) => sperren.oeffnen(s, dayOpen)} onNichtDa={() => sperren.neu(dayOpen)} onBuerotag={() => sperren.neu(dayOpen, "buero")}
        />
      )}
      {edit && (
        <EditTerminDrawer
          t={edit} people={people} kunden={kunden} onClose={() => setEdit(null)}
          onAufnehmen={(modus) => { setAufgenommen({ t: edit, modus, ausTermin: true }); setEdit(null); }}
          onTagesuebersicht={() => {
            const d = (edit.start_at || "").slice(0, 10);
            setEdit(null);
            /* Auf dem Telefon IST die Tagesübersicht die Liste unter dem Kalender. */
            if (telefon) { if (d) { setMonth(firstOfMonth(new Date(`${d}T00:00:00`))); setGewaehlt(d); } } else setDayOpen(d);
          }}
        />
      )}
      {aboOffen && <AboTerminBlatt t={aboOffen} onClose={() => setAboOffen(null)} />}
      {aktOpen && <AktivitaetPanel onClose={() => setAktOpen(false)} />}
      {sperren.dialog}
      {kopierFrage.dialog}
      {schiebFrage.dialog}
    </div>
  );
}
